import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { PRICING } from '@vault/shared';
import { Button, Icon } from '@vault/ui';
import { api, ApiError } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.js';
import { useT } from '../i18n/index.js';
import { CenterShell } from './shared/CenterShell.js';

type Interval = 'monthly' | 'yearly';

/**
 * No-pressure paywall. Free stays generous; Pro connects everything. Reachable
 * both as a standalone page (/pricing) and from the sidebar upgrade button.
 * Checkout hands off to the billing provider; when billing isn't wired yet the
 * API returns 501 and we say so plainly instead of erroring.
 */
export function PricingPage() {
  const navigate = useNavigate();
  const { user, isPro } = useAuth();
  const { t } = useT();
  const [interval, chooseInterval] = useState<Interval>('yearly');

  const plans = useQuery({ queryKey: ['billing', 'plans'], queryFn: () => api.billing.plans() });
  const pricing = plans.data?.pricing.pro ?? PRICING.pro;
  const perMonth = interval === 'yearly' ? Math.round(pricing.yearlyUsd / 12) : pricing.monthlyUsd;
  const discountPct = Math.round((1 - pricing.yearlyUsd / (pricing.monthlyUsd * 12)) * 100);

  const checkout = useMutation({
    mutationFn: (iv: Interval) => api.billing.checkout(iv),
    onSuccess: (res) => {
      window.location.href = res.url;
    },
  });

  const checkoutSoon =
    checkout.isError &&
    checkout.error instanceof ApiError &&
    (checkout.error.status === 501 || checkout.error.code === 'billing_not_configured');
  const checkoutFailed = checkout.isError && !checkoutSoon;

  function upgrade() {
    if (!user) {
      navigate('/login');
      return;
    }
    if (isPro || checkout.isPending) return;
    checkout.mutate(interval);
  }

  function dismiss() {
    if (window.history.length > 1) navigate(-1);
    else navigate(user ? '/timeline' : '/login');
  }

  return (
    <CenterShell maxWidth={780} align="start">
      <style>{PRICING_CSS}</style>
      <div style={modal}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={headline}>{t('pricing.title')}</span>
            <span style={{ fontSize: 13, color: 'var(--text-2)' }}>{t('pricing.subtitle')}</span>
          </div>
          <span style={{ marginInlineStart: 'auto', display: 'inline-flex', border: '1px solid var(--line-strong)', borderRadius: 999, padding: 2, gap: 2 }}>
            <IntervalTab active={interval === 'monthly'} onClick={() => chooseInterval('monthly')}>
              {t('pricing.monthly', { price: pricing.monthlyUsd })}
            </IntervalTab>
            <IntervalTab active={interval === 'yearly'} onClick={() => chooseInterval('yearly')}>
              {t('pricing.yearly', { price: pricing.yearlyUsd })}{' '}
              <span style={{ color: 'var(--success)', fontFamily: 'var(--font-mono)', fontSize: 9 }}>−{discountPct}%</span>
            </IntervalTab>
          </span>
          <button type="button" className="tvpr-x" onClick={dismiss} aria-label={t('common.close')}>
            <Icon name="x" size={11} />
          </button>
        </div>

        <div className="tvpr-plans" style={{ display: 'flex', gap: 16 }}>
          {/* Free */}
          <div style={{ ...planCard, flex: 1, background: 'var(--bg)', border: '1px solid var(--line)' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <span style={planName}>{t('common.free')}</span>
              {!isPro && (
                <span style={yourPlanTag}>{t('pricing.yourPlan')}</span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={price}>$0</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-3)' }}>{t('pricing.forever')}</span>
            </div>
            <ul style={list}>
              <Feature>{t('pricing.free.f1')}</Feature>
              <Feature>{t('pricing.free.f2')}</Feature>
              <Feature>{t('pricing.free.f3')}</Feature>
              <Feature muted>{t('pricing.free.f4')}</Feature>
              <Feature muted>{t('pricing.free.f5')}</Feature>
            </ul>
            <span style={{ marginTop: 'auto' }}>
              <Button variant="ghost" fullWidth onClick={dismiss}>
                {t('pricing.stayFree')}
              </Button>
            </span>
          </div>

          {/* Pro */}
          <div style={{ ...planCard, flex: 1.15, ...proCard }}>
            <span style={mostLoved}>{t('pricing.mostLoved')}</span>
            <span style={planName}>{t('common.pro')}</span>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={price}>${perMonth}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-3)' }}>
                {interval === 'yearly' ? t('pricing.perMonthYearly', { total: pricing.yearlyUsd }) : t('pricing.perMonth')}
              </span>
            </div>
            <ul style={list}>
              <Feature pro>{t('pricing.pro.f1')}</Feature>
              <Feature pro>{t('pricing.pro.f2')}</Feature>
              <Feature pro>{t('pricing.pro.f3')}</Feature>
              <Feature pro>{t('pricing.pro.f4')}</Feature>
              <Feature pro>{t('pricing.pro.f5')}</Feature>
            </ul>
            <span style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Button fullWidth loading={checkout.isPending} onClick={upgrade}>
                {isPro ? t('pricing.youreOnPro') : user ? t('common.upgradeToPro') : t('pricing.signInToUpgrade')}
              </Button>
              {checkoutSoon && (
                <span style={noteInfo}>
                  <Icon name="clock" size={12} color="var(--accent-ink)" />
                  {t('pricing.checkoutSoon')}
                </span>
              )}
              {checkoutFailed && (
                <span style={noteDanger}>
                  <Icon name="x" size={12} color="var(--danger)" />
                  {t('pricing.checkoutFailed')}
                </span>
              )}
            </span>
          </div>
        </div>

        <span style={footer}>{t('pricing.footer')}</span>
      </div>
    </CenterShell>
  );
}

function IntervalTab({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        borderRadius: 999,
        padding: '6px 14px',
        border: 'none',
        cursor: 'pointer',
        fontFamily: 'var(--font-body)',
        fontSize: 11.5,
        fontWeight: active ? 600 : 500,
        color: active ? 'var(--text-1)' : 'var(--text-2)',
        background: active ? 'var(--surface-3)' : 'transparent',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

function Feature({ children, muted, pro }: { children: ReactNode; muted?: boolean; pro?: boolean }) {
  return (
    <li style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5, fontWeight: pro ? 500 : 400, color: muted ? 'var(--text-3)' : 'var(--text-1)' }}>
      {muted ? (
        <Icon name="minus" size={12} color="currentColor" />
      ) : (
        <Icon name="check" size={12} color={pro ? 'var(--primary)' : 'var(--success)'} strokeWidth={2.2} />
      )}
      {children}
    </li>
  );
}

const modal: CSSProperties = {
  background: 'var(--surface-1)',
  border: '1px solid var(--line-strong)',
  borderRadius: 'var(--r-xl)',
  boxShadow: 'var(--shadow-modal)',
  padding: 28,
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
};
const headline: CSSProperties = {
  fontFamily: 'var(--font-display-cond)',
  fontSize: 22,
  fontWeight: 700,
  letterSpacing: '.02em',
  textTransform: 'uppercase',
};
const planCard: CSSProperties = {
  borderRadius: 16,
  padding: 20,
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  position: 'relative',
  minHeight: 320,
};
const proCard: CSSProperties = {
  border: '1px solid var(--line-primary)',
  background: 'linear-gradient(165deg, rgba(59,107,255,.16), transparent 50%), var(--surface-1)',
  boxShadow: 'var(--glow-primary)',
};
const planName: CSSProperties = { fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600 };
const price: CSSProperties = {
  fontFamily: 'var(--font-display-cond)',
  fontSize: 30,
  fontWeight: 700,
  letterSpacing: '.02em',
  textTransform: 'uppercase',
};
const list: CSSProperties = { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 9 };
const yourPlanTag: CSSProperties = {
  display: 'inline-flex',
  background: 'var(--surface-2)',
  border: '1px solid var(--line)',
  borderRadius: 5,
  padding: '2px 7px',
  fontFamily: 'var(--font-mono)',
  fontSize: 8.5,
  color: 'var(--text-2)',
};
const mostLoved: CSSProperties = {
  position: 'absolute',
  top: -11,
  insetInlineEnd: 18,
  display: 'inline-flex',
  background: 'var(--amber)',
  color: 'var(--on-amber)',
  borderRadius: 6,
  padding: '3px 9px',
  fontFamily: 'var(--font-mono)',
  fontSize: 8.5,
  fontWeight: 700,
  letterSpacing: '.08em',
};
const footer: CSSProperties = { fontFamily: 'var(--font-mono)', fontSize: 9.5, color: 'var(--text-3)', textAlign: 'center' };
const noteInfo: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  fontSize: 11.5,
  lineHeight: 1.45,
  color: 'var(--text-2)',
  background: 'var(--accent-tint)',
  border: '1px solid var(--line-accent)',
  borderRadius: 'var(--r-md)',
  padding: '9px 11px',
};
const noteDanger: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  fontSize: 11.5,
  color: 'var(--danger)',
  background: 'var(--danger-tint)',
  border: '1px solid var(--danger-line)',
  borderRadius: 'var(--r-md)',
  padding: '9px 11px',
};

const PRICING_CSS = `
.tvpr-x{display:inline-flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:9px;
  border:1px solid var(--line-strong);background:transparent;color:var(--text-3);cursor:pointer}
.tvpr-x:hover{background:var(--surface-2);color:var(--text-1)}
@media (max-width:640px){.tvpr-plans{flex-direction:column}}
`;
